import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";

export interface ConfettiProps {
  /** Whether the confetti is active */
  isActive: boolean;
  /** Number of confetti pieces */
  count?: number;
  /** Duration of the animation in milliseconds */
  duration?: number;
  /** Callback when the animation completes */
  onComplete?: () => void;
  /** Colors for confetti pieces */
  colors?: string[];
}

interface ConfettiPiece {
  id: number;
  x: number;
  color: string;
  size: number;
  rotation: number;
  shape: "circle" | "square" | "star";
  delay: number;
  duration: number;
  drift: number;
}

/** Default JumpBean celebration colors */
const DEFAULT_COLORS = [
  "#FFD93D", // Sunshine
  "#FF6B9D", // Fairy
  "#9B59B6", // Magic
  "#6BCB77", // Meadow
  "#4A90D9", // Sky
  "#FF8C42", // Pixel
  "#E8A0FF", // Luna
  "#90EE90", // Petal
];

/**
 * Celebratory confetti animation for achievements and completions.
 * Renders an explosion of colorful confetti that falls from the top of the screen.
 *
 * @example
 * // Basic confetti on level completion
 * <Confetti isActive={showCelebration} onComplete={() => setShowCelebration(false)} />
 *
 * @example
 * // Custom confetti for specific discipline
 * <Confetti
 *   isActive={earned3Stars}
 *   count={100}
 *   colors={['#E8A0FF', '#9B59B6', '#FFD93D']}
 * />
 */
export function Confetti({
  isActive,
  count = 50,
  duration = 3000,
  onComplete,
  colors = DEFAULT_COLORS,
}: ConfettiProps) {
  const [pieces, setPieces] = useState<ConfettiPiece[]>([]);
  const [isVisible, setIsVisible] = useState(false);

  const generatePieces = useCallback((): ConfettiPiece[] => {
    const shapes: ConfettiPiece["shape"][] = ["circle", "square", "star"];
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      color: colors[Math.floor(Math.random() * colors.length)] ?? DEFAULT_COLORS[0] ?? "#FFD93D",
      size: Math.random() * 8 + 6,
      rotation: Math.random() * 360,
      shape: shapes[Math.floor(Math.random() * shapes.length)] ?? "circle",
      delay: Math.random() * 500,
      duration: duration * (0.7 + Math.random() * 0.3),
      drift: (Math.random() - 0.5) * 100,
    }));
  }, [count, duration, colors]);

  useEffect(() => {
    if (isActive) {
      setPieces(generatePieces());
      setIsVisible(true);

      const timer = setTimeout(() => {
        setIsVisible(false);
        onComplete?.();
      }, duration + 500);

      return () => clearTimeout(timer);
    }
  }, [isActive, duration, generatePieces, onComplete]);

  if (!isVisible || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 pointer-events-none z-50 overflow-hidden"
      aria-hidden="true"
    >
      {pieces.map((piece) => (
        <ConfettiPieceElement key={piece.id} piece={piece} />
      ))}
    </div>,
    document.body
  );
}

interface ConfettiPieceElementProps {
  piece: ConfettiPiece;
}

function ConfettiPieceElement({ piece }: ConfettiPieceElementProps) {
  const shapeElements: Record<ConfettiPiece["shape"], React.ReactNode> = {
    circle: (
      <div
        className="rounded-full"
        style={{
          width: piece.size,
          height: piece.size,
          backgroundColor: piece.color,
        }}
      />
    ),
    square: (
      <div
        style={{
          width: piece.size,
          height: piece.size,
          backgroundColor: piece.color,
          transform: `rotate(${piece.rotation}deg)`,
        }}
      />
    ),
    star: (
      <svg
        width={piece.size}
        height={piece.size}
        viewBox="0 0 24 24"
        style={{ transform: `rotate(${piece.rotation}deg)` }}
      >
        <path
          d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z"
          fill={piece.color}
        />
      </svg>
    ),
  };

  return (
    <div
      className="absolute animate-[confettiFall_var(--duration)_ease-out_forwards]"
      style={{
        left: `${piece.x}%`,
        top: "-20px",
        animationDelay: `${piece.delay}ms`,
        "--duration": `${piece.duration}ms`,
        "--drift": `${piece.drift}px`,
        "--rotation": `${piece.rotation + 720}deg`,
      } as React.CSSProperties}
    >
      {shapeElements[piece.shape]}
    </div>
  );
}

/**
 * Sparkle burst effect for smaller celebrations
 */
export interface SparkleProps {
  /** Whether the sparkle is active */
  isActive: boolean;
  /** Position relative to parent (or centered if not specified) */
  position?: { x: number; y: number };
  /** Number of sparkle particles */
  count?: number;
  /** Duration in milliseconds */
  duration?: number;
  /** Callback when complete */
  onComplete?: () => void;
}

export function Sparkle({
  isActive,
  position,
  count = 8,
  duration = 800,
  onComplete,
}: SparkleProps) {
  const [particles, setParticles] = useState<Array<{ id: number; angle: number; distance: number; size: number }>>([]);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isActive) {
      setParticles(
        Array.from({ length: count }, (_, i) => ({
          id: i,
          angle: (360 / count) * i + Math.random() * 30 - 15,
          distance: 30 + Math.random() * 30,
          size: 4 + Math.random() * 6,
        }))
      );
      setIsVisible(true);

      const timer = setTimeout(() => {
        setIsVisible(false);
        onComplete?.();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [isActive, count, duration, onComplete]);

  if (!isVisible) return null;

  const x = position?.x ?? 50;
  const y = position?.y ?? 50;

  return (
    <div
      className="absolute inset-0 pointer-events-none overflow-visible"
      style={{ position: "absolute" }}
      aria-hidden="true"
    >
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="absolute animate-[sparkleBurst_var(--duration)_ease-out_forwards]"
          style={{
            left: `${x}%`,
            top: `${y}%`,
            "--angle": `${particle.angle}deg`,
            "--distance": `${particle.distance}px`,
            "--duration": `${duration}ms`,
          } as React.CSSProperties}
        >
          <svg
            width={particle.size}
            height={particle.size}
            viewBox="0 0 24 24"
            className="text-sunshine-400"
          >
            <path
              d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z"
              fill="currentColor"
            />
          </svg>
        </div>
      ))}
    </div>
  );
}

/**
 * Celebration wrapper that combines confetti and sound effects
 */
export interface CelebrationProps {
  /** Type of celebration */
  type: "star" | "badge" | "level" | "perfect";
  /** Whether to trigger the celebration */
  trigger: boolean;
  /** Callback when celebration completes */
  onComplete?: () => void;
  /** Children to wrap */
  children?: React.ReactNode;
}

export function Celebration({ type, trigger, onComplete, children }: CelebrationProps) {
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (trigger && !isPlaying) {
      setIsPlaying(true);
    }
  }, [trigger, isPlaying]);

  const celebrationConfig: Record<CelebrationProps["type"], ConfettiProps> = {
    star: {
      isActive: isPlaying,
      count: 20,
      duration: 1500,
      colors: ["#FFD93D", "#FFEE58", "#FFC107"],
    },
    badge: {
      isActive: isPlaying,
      count: 40,
      duration: 2500,
      colors: DEFAULT_COLORS,
    },
    level: {
      isActive: isPlaying,
      count: 60,
      duration: 3000,
      colors: DEFAULT_COLORS,
    },
    perfect: {
      isActive: isPlaying,
      count: 100,
      duration: 4000,
      colors: DEFAULT_COLORS,
    },
  };

  const handleComplete = () => {
    setIsPlaying(false);
    onComplete?.();
  };

  return (
    <>
      {children}
      <Confetti {...celebrationConfig[type]} onComplete={handleComplete} />
    </>
  );
}

// Add confetti animations if not present
if (typeof document !== "undefined") {
  const styleId = "jumpbean-confetti-animations";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      @keyframes confettiFall {
        0% {
          transform: translateY(0) translateX(0) rotate(0deg);
          opacity: 1;
        }
        100% {
          transform: translateY(100vh) translateX(var(--drift, 0px)) rotate(var(--rotation, 720deg));
          opacity: 0;
        }
      }
      @keyframes sparkleBurst {
        0% {
          transform: translateX(0) translateY(0) scale(0);
          opacity: 1;
        }
        100% {
          transform:
            translateX(calc(cos(var(--angle)) * var(--distance)))
            translateY(calc(sin(var(--angle)) * var(--distance)))
            scale(1);
          opacity: 0;
        }
      }
    `;
    document.head.appendChild(style);
  }
}

export default Confetti;
